/**
 * Video Analytics Helper Functions
 *
 * Utilities for extracting video metadata and creating analytics configurations
 * for video tracking (YouTube, CMS CDN, etc.)
 */

import type { MediaSource } from '../../types';
import { slugify } from '../string';

/**
 * Determines the video provider type from MediaSource
 */
export function getVideoProvider(mediaSource: MediaSource): 'youtube' | 'cms-cdn' | 'unknown' {
  if (mediaSource.type === 'embed') return 'youtube';
  if (mediaSource.type === 'embedCmsVideo') return 'cms-cdn';
  return 'unknown';
}

/**
 * Extracts video ID from URL based on provider type
 */
export function extractVideoId(url: string, provider: 'youtube' | 'cms-cdn'): string {
  if (!url) return '';

  if (provider === 'youtube') {
    try {
      const urlObj = new URL(url);

      // /embed/VIDEO_ID format
      if (urlObj.pathname.includes('/embed/')) {
        return urlObj.pathname.split('/embed/')[1]?.split('?')[0] || '';
      }

      // /watch?v=VIDEO_ID format
      if (urlObj.searchParams.has('v')) {
        return urlObj.searchParams.get('v') || '';
      }

      // youtu.be/VIDEO_ID format
      if (urlObj.hostname.includes('youtu.be')) {
        return urlObj.pathname.substring(1).split('?')[0] || '';
      }

      return '';
    } catch {
      return '';
    }
  }

  if (provider === 'cms-cdn') {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const segments = pathname.split('/');
      const lastSegment = segments[segments.length - 1];
      return lastSegment?.replace(/\.[^.]+$/, '') || '';
    } catch {
      return '';
    }
  }

  return '';
}

/**
 * Gets video URL from MediaSource
 */
export function getVideoUrl(mediaSource: MediaSource): string {
  if (mediaSource.type === 'embed') {
    return mediaSource.embedUrl || '';
  }
  if (mediaSource.type === 'embedCmsVideo') {
    return mediaSource.videoUrl || '';
  }
  return '';
}

/**
 * Gets video title from multiple sources with priority fallback
 * Priority: explicit videoMetadata.title > mediaSource.alt > ''
 */
export function getVideoTitle(
  mediaSource: MediaSource,
  videoMetadata?: { title?: string }
): string {
  // Check for videoTitle in video-type media sources
  if (
    ['video', 'embed', 'embedCmsVideo'].includes(mediaSource.type) &&
    'videoTitle' in mediaSource &&
    mediaSource.videoTitle
  ) {
    return mediaSource.videoTitle;
  }

  if (videoMetadata?.title) {
    return videoMetadata.title;
  }

  if ('alt' in mediaSource && mediaSource.alt) {
    return mediaSource.alt;
  }

  return '';
}

/**
 * Video analytics configuration returned from helper
 */
export interface VideoAnalyticsConfig {
  videoId: string;
  videoTitle: string;
  videoUrl: string;
  videoProvider: 'youtube' | 'cms-cdn' | 'unknown';
  sectionHeader?: string;
}

export function createVideoAnalyticsConfig(
  mediaSource: MediaSource,
  videoMetadata?: { title?: string; id?: string },
  sectionHeader?: string
): VideoAnalyticsConfig {
  const provider = getVideoProvider(mediaSource);
  const url = getVideoUrl(mediaSource);

  const extractedTitle = getVideoTitle(mediaSource, videoMetadata);
  const videoTitle = extractedTitle || (sectionHeader ? `${sectionHeader} video` : '');
  const videoId = extractedTitle
    ? slugify(extractedTitle)
    : videoMetadata?.id || (provider !== 'unknown' ? extractVideoId(url, provider) : '');

  return {
    videoId,
    videoTitle,
    videoUrl: url,
    videoProvider: provider,
    sectionHeader,
  };
}
